import { db } from '../firebase';
import {
  collection,
  doc,
  getDocs,
  setDoc,
  deleteDoc,
  updateDoc,
  onSnapshot,
  Timestamp,
  query,
  orderBy
} from 'firebase/firestore';
import { normalizeTicker } from '../lib/normalizeTicker';

// ─── LOT ─────────────────────────────────────────────────────────────────────

export interface Lot {
  id: string;             // crypto.randomUUID() on creation
  type: 'buy' | 'sell';
  shares: number;
  totalCostPaid: number;  // total dollars paid for this lot
  costPerShare: number;   // derived: totalCostPaid / shares
  datePurchased: string;  // ISO date string
  dateSold?: string;      // ISO date string — undefined if still held
  source: 'manual' | 'schwab' | 'fidelity' | 'other';
}

// ─── HOLDING ─────────────────────────────────────────────────────────────────

export interface Holding {
  _docId?: string; // Firestore document ID — may differ from ticker for legacy holdings stored before normalizeTicker was applied
  ticker: string;                      // uppercase, primary key
  companyName: string;
  lots: Lot[];                         // all purchase/sale lots — source of truth
  dateAdded: string;                   // ISO date string
  notes?: string;
  source?: string;                     // top-level source tag, e.g. 'schwab', 'manual'
  lastKeeperVerdict?: 'KEEP' | 'WATCH' | 'EXIT';
  lastKeeperDate?: string;
  lastAssessment?: any; // We can type as KeeperAssessment but maybe just any or KeeperAssessment
  lastOracleAnalysisDate?: string;
}

// ─── ROLLUP HELPER ───────────────────────────────────────────────────────────

export interface HoldingTotals {
  totalShares: number;
  totalCostBasis: number;
  avgCostPerShare: number;
}

export function computeTotals(lots: Lot[]): HoldingTotals {
  const buys = lots.filter(l => l.type === 'buy' || (!(l as any).type && !l.dateSold));
  const sells = lots.filter(l => l.type === 'sell');

  const totalSharesBought = buys.reduce((sum, l) => sum + l.shares, 0);
  const totalSharesSold = sells.reduce((sum, l) => sum + l.shares, 0);
  const totalShares = Math.max(0, totalSharesBought - totalSharesSold);
  const totalCostBasis = buys.reduce((sum, l) => sum + l.totalCostPaid, 0);
  const avgCostPerShare = totalSharesBought > 0 ? totalCostBasis / totalSharesBought : 0;

  return { totalShares, totalCostBasis, avgCostPerShare };
}

// ─── CRUD ────────────────────────────────────────────────────────────────────

export async function addHolding(uid: string, holding: Holding): Promise<void> {
  const safeTicker = normalizeTicker(holding.ticker);
  const ref = doc(db, 'users', uid, 'holdings', safeTicker);
  await setDoc(ref, {
    ...holding,
    ticker: safeTicker,
    dateAdded: holding.dateAdded || new Date().toISOString(),
    createdAt: Timestamp.now(),
  });
}

export async function updateHolding(
  uid: string,
  ticker: string,
  updates: Partial<Holding>
): Promise<void> {
  const safeTicker = normalizeTicker(ticker);
  const ref = doc(db, 'users', uid, 'holdings', safeTicker);
  await setDoc(ref, updates, { merge: true });
}

// Fire-and-forget: stamps lastOracleAnalysisDate on a holding only if
// the holding document already exists. Silently no-ops for tickers that
// are not in the user's portfolio.
export async function stampOracleAnalysisDate(uid: string, ticker: string): Promise<void> {
  try {
    const safeTicker = normalizeTicker(ticker);
    const ref = doc(db, 'users', uid, 'holdings', safeTicker);
    await updateDoc(ref, { lastOracleAnalysisDate: new Date().toISOString() });
  } catch {
    // Doc doesn't exist (ticker not in portfolio) — silently ignore.
  }
}

export async function removeHolding(uid: string, ticker: string, docId?: string): Promise<void> {
  // Use the Firestore document ID directly when available — legacy holdings may have a
  // doc ID that differs from normalizeTicker(ticker) (e.g. BRKB stored before alias was added).
  const targetId = docId ?? normalizeTicker(ticker);
  const ref = doc(db, 'users', uid, 'holdings', targetId);
  await deleteDoc(ref);
}

export async function getHoldings(uid: string): Promise<Holding[]> {
  const ref = collection(db, 'users', uid, 'holdings');
  const q = query(ref, orderBy('createdAt', 'desc'));
  const snapshot = await getDocs(q);
  return snapshot.docs.map(d => ({ ...normalizeLegacyHolding(d.data()), _docId: d.id }));
}

export function subscribeToHoldings(
  uid: string,
  onUpdate: (holdings: Holding[]) => void
): () => void {
  const ref = collection(db, 'users', uid, 'holdings');
  const q = query(ref, orderBy('createdAt', 'desc'));
  return onSnapshot(q, snapshot => {
    onUpdate(snapshot.docs.map(d => ({ ...normalizeLegacyHolding(d.data()), _docId: d.id })));
  });
}

// ─── LEGACY MIGRATION ────────────────────────────────────────────────────────
// Converts old flat-field documents (shares + costBasisPerShare) into the
// new Lot structure transparently, so existing Firestore data keeps working.

function normalizeLegacyHolding(data: any): Holding {
  if (data.lots && Array.isArray(data.lots) && data.lots.length > 0) {
    return data as Holding; // already migrated
  }

  // Legacy document: synthesize a single lot from flat fields
  const shares = data.shares ?? 0;
  const costPerShare = data.costBasisPerShare ?? 0;
  const totalCostPaid = shares * costPerShare;

  const syntheticLot: Lot = {
    id: 'legacy-' + data.ticker,
    type: 'buy',
    shares,
    totalCostPaid,
    costPerShare,
    datePurchased: data.dateAdded || new Date().toISOString(),
    source: (data.source as Lot['source']) || 'manual',
  };

  return {
    ticker: data.ticker,
    companyName: data.companyName,
    lots: shares > 0 || costPerShare > 0 ? [syntheticLot] : [],
    dateAdded: data.dateAdded || new Date().toISOString(),
    notes: data.notes,
    source: data.source,
    lastKeeperVerdict: data.lastKeeperVerdict,
    lastKeeperDate: data.lastKeeperDate,
    lastAssessment: data.lastAssessment,
  };
}
